import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from 'framer-motion';
import { selectDisplayedGradient, selectGradientA, selectGradientB, selectGradientData } from '../../store/system/system.selector.js';
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase/firebase.utils.js";

// styles
import {
    GradientBGContainer,
    DynamicGradientDiv
} from "./GradientBG.styles.js";

import { setTeamIndex } from "../../store/user/user.action.js";
import { selectUser, selectTeamIndex } from "../../store/user/user.selector.js";
import { setSystemNotification } from "../../store/system/system.action.js";

export default function GradientBG({ setGradient = null }) {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const teamIndex = useSelector(selectTeamIndex);
    const displayedGradient = useSelector(selectDisplayedGradient);
    const gradientA = useSelector(selectGradientA);
    const gradientB = useSelector(selectGradientB);
    const gradientData = useSelector(selectGradientData);

    const prevTeamIndex = useRef(undefined);
    const hasInitialized = useRef(false);

    // FETCH teamIndex - Only if not already in Redux or if user changes
    useEffect(() => {
        if (!user) return;

        // If we already have teamIndex in Redux and haven't initialized yet, skip the fetch
        if (teamIndex !== undefined && teamIndex !== null && !hasInitialized.current) {
            hasInitialized.current = true;
            prevTeamIndex.current = teamIndex;
            return;
        }

        // Only fetch if we don't have teamIndex or if user changed
        if (teamIndex === undefined || teamIndex === null || !hasInitialized.current) {
            hasInitialized.current = true;
            
            const userDocRef = doc(db, `users/${user.uid}`);

            // Use getDoc instead of onSnapshot for one-time fetch
            getDoc(userDocRef).then((docSnap) => {
                if (docSnap.exists()) {
                    const { teamIndex: fetchedTeamIndex } = docSnap.data();

                    if (fetchedTeamIndex !== undefined && fetchedTeamIndex !== null) {
                        if (prevTeamIndex.current !== fetchedTeamIndex) {
                            dispatch(setSystemNotification(`Setting theme to: ${gradientData[fetchedTeamIndex].team }`));
                            prevTeamIndex.current = fetchedTeamIndex;
                        }
                        dispatch(setTeamIndex(fetchedTeamIndex));
                    } else if (prevTeamIndex.current !== "missing") {
                        dispatch(setSystemNotification("GradientBG: teamIndex is undefined or null"));
                        prevTeamIndex.current = "missing";
                    }
                } else {
                    dispatch(setSystemNotification("GradientBG: Could not fetch teamIndex, document does not exist"));
                }
            }).catch((error) => {
                dispatch(setSystemNotification("GradientBG: Error fetching teamIndex ->", error.message));
            });
        }
    }, [user, teamIndex, dispatch, gradientData]);

    return (
        <>
            {setGradient === null ?
                <>
                    {gradientA && gradientB &&
                        <GradientBGContainer $bg={displayedGradient === 'A' ? gradientData[gradientA] : gradientData[gradientB]} >
                            <motion.div
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: displayedGradient === 'A' ? 1 : 0 }}
                                transition={{ duration: 1.0 }}
                            >
                                <DynamicGradientDiv $gradient={gradientData[gradientA]} />
                            </motion.div>
                            <motion.div
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: displayedGradient === 'B' ? 1 : 0 }}
                                transition={{ duration: 1.0 }}
                            >
                                <DynamicGradientDiv $gradient={gradientData[gradientB]} />
                            </motion.div>
                        </GradientBGContainer>
                    }
                </>
                :
                <GradientBGContainer $bg={setGradient} >
                    <motion.div
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: displayedGradient === 'A' ? 1 : 0 }}
                        transition={{ duration: 1.0 }}
                    >
                        <DynamicGradientDiv $gradient={setGradient} />
                    </motion.div>
                    <motion.div
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: displayedGradient === 'B' ? 1 : 0 }}
                        transition={{ duration: 1.0 }}
                    >
                        <DynamicGradientDiv $gradient={setGradient} />
                    </motion.div>
                </GradientBGContainer>
            }
        </>
    );
}